/**
 * Log Viewer - TypeScript Module
 * Visor de logs del sistema con funcionalidad de tiempo real
 */
export class LogViewer {
    constructor() {
        this.refreshInterval = null;
        this.firstLogId = null;
        this.lastLogId = null;
        this.isLoadingMore = false;
        this.hasOlderLogs = true;
        this.currentFontSize = 14;
        this.currentHeight = 500;
        this.autoScrollEnabled = true;
        this.scrollTimeout = null;
        this.initialize();
    }
    initialize() {
        this.loadLogs();
        this.setupAutoRefresh();
        this.setupFilters();
        this.setupFontSizeControl();
        this.setupHeightControl();
        this.setupInfiniteScroll();
        this.setupAutoScroll();
    }
    setupFontSizeControl() {
        const slider = document.getElementById('fontSizeSlider');
        const increaseBtn = document.getElementById('fontIncrease');
        const decreaseBtn = document.getElementById('fontDecrease');
        const display = document.getElementById('fontSizeDisplay');
        const terminalBody = document.getElementById('terminalBody');
        const updateFontSize = (size) => {
            this.currentFontSize = Math.max(10, Math.min(24, parseInt(size)));
            if (slider)
                slider.value = this.currentFontSize.toString();
            if (display)
                display.textContent = this.currentFontSize + 'px';
            if (terminalBody)
                terminalBody.style.setProperty('--terminal-font-size', this.currentFontSize + 'px');
            localStorage.setItem('logViewerFontSize', this.currentFontSize.toString());
        };
        const savedFontSize = localStorage.getItem('logViewerFontSize');
        if (savedFontSize) {
            updateFontSize(savedFontSize);
        }
        slider?.addEventListener('input', function () {
            updateFontSize(this.value);
        });
        increaseBtn?.addEventListener('click', () => {
            updateFontSize((this.currentFontSize + 1).toString());
        });
        decreaseBtn?.addEventListener('click', () => {
            updateFontSize((this.currentFontSize - 1).toString());
        });
    }
    setupHeightControl() {
        const slider = document.getElementById('heightSlider');
        const increaseBtn = document.getElementById('heightIncrease');
        const decreaseBtn = document.getElementById('heightDecrease');
        const display = document.getElementById('heightDisplay');
        const terminalBody = document.getElementById('terminalBody');
        const updateHeight = (size) => {
            this.currentHeight = Math.max(300, Math.min(1000, parseInt(size.toString())));
            if (slider)
                slider.value = this.currentHeight.toString();
            if (display)
                display.textContent = this.currentHeight + 'px';
            if (terminalBody)
                terminalBody.style.setProperty('--terminal-height', this.currentHeight + 'px');
            localStorage.setItem('logViewerHeight', this.currentHeight.toString());
        };
        const savedHeight = localStorage.getItem('logViewerHeight');
        if (savedHeight) {
            updateHeight(savedHeight);
        }
        slider?.addEventListener('input', function () {
            updateHeight(this.value);
        });
        increaseBtn?.addEventListener('click', () => {
            updateHeight(this.currentHeight + 50);
        });
        decreaseBtn?.addEventListener('click', () => {
            updateHeight(this.currentHeight - 50);
        });
    }
    setupAutoScroll() {
        const toggle = document.getElementById('autoScrollToggle');
        const terminalBody = document.getElementById('terminalBody');
        toggle?.addEventListener('change', () => {
            this.autoScrollEnabled = toggle.checked;
            if (this.autoScrollEnabled) {
                this.scrollToBottom();
            }
        });
        terminalBody?.addEventListener('scroll', () => {
            if (this.scrollTimeout) {
                clearTimeout(this.scrollTimeout);
            }
            this.scrollTimeout = window.setTimeout(() => {
                // User scrolled
            }, 2000);
        });
    }
    scrollToBottom(smooth = true) {
        const terminalBody = document.getElementById('terminalBody');
        if (terminalBody) {
            terminalBody.scrollTo({
                top: terminalBody.scrollHeight,
                behavior: smooth ? 'smooth' : 'auto'
            });
        }
    }
    setupInfiniteScroll() {
        const terminalBody = document.getElementById('terminalBody');
        terminalBody?.addEventListener('scroll', () => {
            const scrollPosition = terminalBody.scrollTop;
            if (scrollPosition < 100 && !this.isLoadingMore && this.hasOlderLogs) {
                this.loadOlderLogs();
            }
        });
    }
    setupAutoRefresh() {
        const toggle = document.getElementById('autoRefreshToggle');
        const startAutoRefresh = () => {
            if (this.refreshInterval)
                clearInterval(this.refreshInterval);
            this.refreshInterval = window.setInterval(() => {
                this.loadNewLogs();
            }, 5000);
        };
        toggle?.addEventListener('change', () => {
            if (toggle.checked) {
                startAutoRefresh();
            }
            else if (this.refreshInterval) {
                clearInterval(this.refreshInterval);
                this.refreshInterval = null;
            }
        });
        startAutoRefresh();
    }
    setupFilters() {
        const filters = ['filterLevel', 'filterSource', 'filterMethod', 'filterDateFrom', 'filterDateTo', 'filterSearch'];
        filters.forEach((filterId) => {
            const element = document.getElementById(filterId);
            if (element) {
                element.addEventListener('change', () => {
                    this.firstLogId = null;
                    this.lastLogId = null;
                    this.hasOlderLogs = true;
                    this.loadLogs();
                });
            }
        });
        const clearBtn = document.getElementById('clearFilters');
        clearBtn?.addEventListener('click', () => {
            filters.forEach((filterId) => {
                const element = document.getElementById(filterId);
                if (element) {
                    element.value = '';
                }
            });
            this.firstLogId = null;
            this.lastLogId = null;
            this.hasOlderLogs = true;
            this.loadLogs();
        });
    }
    getFilters() {
        return {
            level: document.getElementById('filterLevel')?.value || '',
            source: document.getElementById('filterSource')?.value || '',
            method: document.getElementById('filterMethod')?.value || '',
            date_from: document.getElementById('filterDateFrom')?.value || '',
            date_to: document.getElementById('filterDateTo')?.value || '',
            search: document.getElementById('filterSearch')?.value || '',
            limit: 50
        };
    }
    async loadLogs() {
        const filters = this.getFilters();
        const queryParams = {
            level: filters.level,
            source: filters.source,
            method: filters.method,
            date_from: filters.date_from,
            date_to: filters.date_to,
            search: filters.search,
            limit: filters.limit.toString()
        };
        const queryString = new URLSearchParams(queryParams).toString();
        try {
            const response = await fetch(`/log-viewer/get-logs?${queryString}`, {
                method: 'GET',
                headers: { 'Accept': 'application/json' }
            });
            if (!response.ok)
                throw new Error('Error al cargar los logs');
            const result = await response.json();
            if (result.success) {
                this.renderLogs(result.data);
                this.hasOlderLogs = result.has_more;
                this.updateLastUpdate();
                const logsCountElement = document.getElementById('logsCount');
                if (logsCountElement) {
                    logsCountElement.textContent = result.count;
                }
                if (this.autoScrollEnabled) {
                    setTimeout(() => this.scrollToBottom(), 100);
                }
            }
        }
        catch (error) {
            console.error('Error loading logs:', error);
            const logsContainer = document.getElementById('logsContainer');
            if (logsContainer) {
                logsContainer.innerHTML = '<div class="text-center text-danger">Error al cargar los logs</div>';
            }
        }
    }
    async loadNewLogs() {
        if (this.isLoadingMore || this.firstLogId === null)
            return;
        const filters = this.getFilters();
        filters.first_id = this.firstLogId;
        filters.limit = 50;
        const queryParams = {
            level: filters.level,
            source: filters.source,
            method: filters.method,
            date_from: filters.date_from,
            date_to: filters.date_to,
            search: filters.search,
            limit: filters.limit.toString(),
            first_id: filters.first_id?.toString()
        };
        const queryString = new URLSearchParams(queryParams).toString();
        try {
            const response = await fetch(`/log-viewer/get-logs?${queryString}`, {
                method: 'GET',
                headers: { 'Accept': 'application/json' }
            });
            if (!response.ok)
                throw new Error('Error al cargar los logs nuevos');
            const result = await response.json();
            if (result.success && result.data.length > 0) {
                this.appendNewLogs(result.data);
                if (this.autoScrollEnabled) {
                    setTimeout(() => this.scrollToBottom(), 100);
                }
                this.updateLastUpdate();
                const logsCountElement = document.getElementById('logsCount');
                if (logsCountElement) {
                    const currentCount = parseInt(logsCountElement.textContent || '0');
                    logsCountElement.textContent = (currentCount + result.data.length).toString();
                }
            }
        }
        catch (error) {
            console.error('Error loading new logs:', error);
        }
    }
    async loadOlderLogs() {
        if (this.isLoadingMore || !this.hasOlderLogs)
            return;
        const container = document.getElementById('logsContainer');
        if (!container)
            return;
        const existingLogs = container.querySelectorAll('.log-entry');
        if (existingLogs.length > 0) {
            const firstLog = existingLogs[0];
            const firstLogIdValue = firstLog.getAttribute('data-log-id');
            if (firstLogIdValue) {
                this.lastLogId = parseInt(firstLogIdValue);
            }
        }
        const filters = this.getFilters();
        filters.limit = 20;
        const queryParams = {
            level: filters.level,
            source: filters.source,
            method: filters.method,
            date_from: filters.date_from,
            date_to: filters.date_to,
            search: filters.search,
            limit: filters.limit.toString()
        };
        if (this.lastLogId) {
            queryParams.last_id = this.lastLogId.toString();
        }
        const queryString = new URLSearchParams(queryParams).toString();
        this.isLoadingMore = true;
        const loadingMoreLogs = document.getElementById('loadingMoreLogs');
        if (loadingMoreLogs) {
            loadingMoreLogs.classList.remove('hidden');
        }
        try {
            const response = await fetch(`/log-viewer/get-logs?${queryString}`, {
                method: 'GET',
                headers: { 'Accept': 'application/json' }
            });
            if (!response.ok)
                throw new Error('Error al cargar los logs antiguos');
            const result = await response.json();
            if (result.success) {
                if (result.data.length > 0) {
                    this.prependOldLogs(result.data);
                }
                else {
                    this.hasOlderLogs = false;
                    const noOlderLogs = document.getElementById('noOlderLogs');
                    if (noOlderLogs) {
                        noOlderLogs.classList.remove('hidden');
                        noOlderLogs.classList.add('show');
                    }
                }
            }
        }
        catch (error) {
            console.error('Error loading older logs:', error);
        }
        finally {
            this.isLoadingMore = false;
            if (loadingMoreLogs) {
                loadingMoreLogs.classList.add('hidden');
            }
        }
    }
    renderLogs(logs) {
        const container = document.getElementById('logsContainer');
        if (!logs || logs.length === 0) {
            if (container) {
                container.innerHTML = '<div class="text-center text-muted"><i class="bi bi-inbox me-2"></i>No se encontraron logs</div>';
            }
            this.firstLogId = null;
            this.lastLogId = null;
            this.hasOlderLogs = false;
            return;
        }
        if (container) {
            container.innerHTML = '';
        }
        const reversedLogs = [...logs].reverse();
        reversedLogs.forEach((log) => {
            const logEntry = this.createLogEntryElement(log);
            if (container) {
                container.appendChild(logEntry);
            }
        });
        if (reversedLogs.length > 0) {
            this.lastLogId = reversedLogs[0].id;
            this.firstLogId = reversedLogs[reversedLogs.length - 1].id;
        }
        const noOlderLogs = document.getElementById('noOlderLogs');
        if (noOlderLogs) {
            if (this.hasOlderLogs) {
                noOlderLogs.classList.add('hidden');
                noOlderLogs.classList.remove('show');
            }
            else {
                noOlderLogs.classList.remove('hidden');
                noOlderLogs.classList.add('show');
            }
        }
        this.applyDynamicStyles();
    }
    appendNewLogs(logs) {
        const container = document.getElementById('logsContainer');
        if (!logs || logs.length === 0 || !container)
            return;
        const noLogsMsg = container.querySelector('.text-center.text-muted');
        if (noLogsMsg && noLogsMsg.textContent?.includes('No se encontraron logs')) {
            container.innerHTML = '';
        }
        const reversedLogs = [...logs].reverse();
        reversedLogs.forEach((log) => {
            const logEntry = this.createLogEntryElement(log);
            container.appendChild(logEntry);
        });
        if (reversedLogs.length > 0) {
            this.firstLogId = reversedLogs[reversedLogs.length - 1].id;
        }
        this.applyDynamicStyles();
    }
    prependOldLogs(logs) {
        const container = document.getElementById('logsContainer');
        const terminalBody = document.getElementById('terminalBody');
        if (!logs || logs.length === 0 || !container) {
            this.hasOlderLogs = false;
            const noOlderLogs = document.getElementById('noOlderLogs');
            if (noOlderLogs) {
                noOlderLogs.classList.remove('hidden');
                noOlderLogs.classList.add('show');
            }
            return;
        }
        const noLogsMsg = container.querySelector('.text-center.text-muted');
        if (noLogsMsg && noLogsMsg.textContent?.includes('No se encontraron logs')) {
            container.innerHTML = '';
        }
        const fragment = document.createDocumentFragment();
        logs.forEach((log) => {
            const logEntry = this.createLogEntryElement(log, true);
            fragment.appendChild(logEntry);
        });
        const oldScrollHeight = terminalBody?.scrollHeight || 0;
        container.insertBefore(fragment, container.firstChild);
        if (logs.length > 0) {
            this.lastLogId = logs[logs.length - 1].id;
        }
        const newScrollHeight = terminalBody?.scrollHeight || 0;
        if (terminalBody) {
            terminalBody.scrollTop = newScrollHeight - oldScrollHeight;
        }
        this.applyDynamicStyles();
    }
    createLogEntryElement(log, isLoading = false) {
        const logEntry = document.createElement('div');
        logEntry.className = `log-entry ${isLoading ? 'log-entry-loading' : 'log-entry-new'}`;
        logEntry.setAttribute('data-log-id', log.id.toString());
        logEntry.setAttribute('data-level-color', log.level_color);
        logEntry.innerHTML = this.buildLogEntryHTML(log);
        logEntry.addEventListener('click', () => this.showLogDetail(log.id));
        return logEntry;
    }
    buildLogEntryHTML(log) {
        return `
            <div class="d-flex align-items-start gap-2">
                <i class="bi ${log.level_icon} mt-1 log-level-icon" data-level-color="${log.level_color}"></i>
                <div class="flex-grow-1">
                    <div>
                        <span class="log-timestamp">[${log.formatted_date}]</span>
                        <span class="log-module">&lt;${this.escapeHtml(log.module_name || 'N/A')}&gt;</span>
                    </div>
                    <div class="log-message mt-1">
                        ${this.escapeHtml(log.message)}
                    </div>
                    <div class="log-more-btn mt-1">
                        <i class="bi bi-chevron-right me-1"></i>Ver más
                    </div>
                </div>
                <span class="badge log-level-badge" data-level-color="${log.level_color}">
                    ${log.level}
                </span>
            </div>
        `;
    }
    async showLogDetail(logId) {
        try {
            const response = await fetch(`/log-viewer/get-log-detail/${logId}`, {
                method: 'GET',
                headers: { 'Accept': 'application/json' }
            });
            if (!response.ok)
                throw new Error('Error al cargar el detalle del log');
            const result = await response.json();
            if (result.success) {
                this.renderLogDetail(result.data);
                const modalElement = document.getElementById('logDetailModal');
                if (modalElement) {
                    const bootstrap = window.bootstrap;
                    const modal = new bootstrap.Modal(modalElement);
                    modal.show();
                }
            }
        }
        catch (error) {
            console.error('Error loading log detail:', error);
        }
    }
    renderLogDetail(log) {
        const container = document.getElementById('logDetailContent');
        let html = `
            <div class="mb-3">
                <div class="row g-2">
                    <div class="col-md-6">
                        <span class="detail-label">ID:</span>
                        <span class="detail-value">#${log.id}</span>
                    </div>
                    <div class="col-md-6">
                        <span class="detail-label">Fecha:</span>
                        <span class="detail-value">${log.formatted_date}</span>
                    </div>
                    <div class="col-md-6">
                        <span class="detail-label">Nivel:</span>
                        <span class="badge log-level-badge" data-level-color="${log.level_color}">${log.level}</span>
                    </div>
                    <div class="col-md-6">
                        <span class="detail-label">Fuente:</span>
                        <span class="detail-value">${log.source}</span>
                    </div>
                </div>
            </div>

            <div class="mb-3">
                <h6 class="detail-label mb-2">Mensaje:</h6>
                <p class="detail-value">${this.escapeHtml(log.message)}</p>
            </div>
        `;
        if (log.content) {
            html += `
                <div class="mb-3">
                    <h6 class="detail-label mb-2">Contenido:</h6>
                    <pre>${this.syntaxHighlight(JSON.stringify(log.content, null, 2))}</pre>
                </div>
            `;
        }
        if (log.response) {
            html += `
                <div class="mb-3">
                    <h6 class="detail-label mb-2">Respuesta:</h6>
                    <pre>${this.syntaxHighlight(JSON.stringify(log.response, null, 2))}</pre>
                </div>
            `;
        }
        if (container) {
            container.innerHTML = html;
        }
        this.applyDynamicStyles();
    }
    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }
    syntaxHighlight(json) {
        json = json.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        return json.replace(/("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\\s*:)?|\\b(true|false|null)\\b|-?\\d+(?:\\.\\d*)?(?:[eE][+\\-]?\\d+)?)/g, (match) => {
            let cls = 'json-number';
            if (/^"/.test(match)) {
                if (/:$/.test(match)) {
                    cls = 'json-key';
                }
                else {
                    cls = 'json-string';
                }
            }
            else if (/true|false/.test(match)) {
                cls = 'json-boolean';
            }
            else if (/null/.test(match)) {
                cls = 'json-null';
            }
            return `<span class="${cls}">${match}</span>`;
        });
    }
    applyDynamicStyles() {
        document.querySelectorAll('.log-entry').forEach((entry) => {
            const htmlEntry = entry;
            const levelColor = entry.getAttribute('data-level-color');
            if (levelColor) {
                htmlEntry.style.setProperty('--log-level-border-color', levelColor);
            }
        });
        document.querySelectorAll('.log-level-icon').forEach((icon) => {
            const htmlIcon = icon;
            const levelColor = icon.getAttribute('data-level-color');
            if (levelColor) {
                htmlIcon.style.setProperty('--log-level-color', levelColor);
            }
        });
        document.querySelectorAll('.log-level-badge').forEach((badge) => {
            const htmlBadge = badge;
            const levelColor = badge.getAttribute('data-level-color');
            if (levelColor) {
                htmlBadge.style.setProperty('--log-level-color', levelColor);
            }
        });
    }
    updateLastUpdate() {
        const now = new Date();
        const timeString = now.toLocaleTimeString('es-ES');
        const lastUpdateElement = document.getElementById('lastUpdate');
        if (lastUpdateElement) {
            lastUpdateElement.textContent = timeString;
        }
    }
}
